import React, { useMemo } from "react";
import { PropTypes } from "prop-types";
import { Navigate, matchPath, useLocation } from "react-router-dom";
import AppHOC from "../AppHOC";
import Spinner from "../../common/Spinner";
import ErrorComponent from "../../common/ErrorComponent";
import OverlayNotProvisioned from "../../common/OverlayNotProvisioned";
import Unauthorized from "../../common/Unauthorized";
import MegaMenu from "../MegaMenu";
import GridContainerWorkflow from "../GridContainerWorkflow";
import GridContainer from "../GridContainer";
import { getCookie } from "../../serviceWorker";
import {
  getLocalStorageFlag,
  isAdminRole,
  isAnalytics,
  isReportingApi,
  removeLocalStorageFlag
} from "../../utils/common";
import { routes } from "../../config/routeConfig";
import { routeURLs } from "../../utils/enums";

const isReportGenerationUI =
  process.env.REACT_APP_SKIP_RBAC_DISABLE_RBAC === "FALSE" ? false : true;

const Authorization = ({ globalFilter, pageProps, setAltoProps }) => {
  const { features, isOverlayNotProvisioned } = globalFilter;
  const location = useLocation();
  const isUIconvergence = Boolean(getCookie("cl-analytics"));
  const isAccountsPage = matchPath(
    { path: routeURLs.accounts },
    location.pathname
  );

  const accessData = useMemo(() => {
    const result = {
      redirectToAccounts: false,
      unauthorized: false,
      disabled: false,
      loader: false,
      error: null,
      sidebar: true,
      overlayNotProvisioned: false
    };
    if (isAnalytics() === true) {
      if (!isAccountsPage && isReportGenerationUI === false) {
        const currentOverlay = getLocalStorageFlag("currentOverlay");
        const currentOverlayName = getLocalStorageFlag("currentOverlayName");
        const roles = getLocalStorageFlag("roles");
        if (
          !(currentOverlay && currentOverlayName && roles)
          || features.error !== null
        ) {
          removeLocalStorageFlag("currentOverlay");
          removeLocalStorageFlag("currentOverlayName");
          removeLocalStorageFlag("roles");
          result.redirectToAccounts = true;
        }
      }
    }
    if (result.redirectToAccounts === false) {
      for (const key in routes) {
        const route = routes[key];
        const routePaths = Array.isArray(route.path) ? route.path : [route.path];
        if (routePaths.some(path => matchPath({ path }, location.pathname))) {
          if (route.access.admin === true) {
            let roles = localStorage.getItem("roles");
            if (roles) roles = JSON.parse(roles);
            result.unauthorized = isAdminRole(roles) === false;
          }
          result.sidebar = route.access.sidebar;
          result.overlayNotProvisioned = isOverlayNotProvisioned === true;
          if (
            result.unauthorized === false && !isAccountsPage
            && isReportGenerationUI === false
          ) {
            if (
              features.loader === true
              || (features.fabric === null && isReportingApi() === false)
            ) result.loader = true;
            else if (features.error !== null) result.error = features.error;
            else if (Array.isArray(route.features) && route.features.length > 0) {
              result.unauthorized = true;
              result.disabled = true;
              for (const item of route.features) {
                if (features.available.includes(item))
                  result.unauthorized = false;
                if (features.enabled.includes(item)) result.disabled = false;
              }
            }
          }
          break;
        }
      }
    }
    return result;
  }, [
    features,
    isOverlayNotProvisioned,
    location.pathname,
  ]);

  return (
    <>
      {accessData.redirectToAccounts === true ? (
        <Navigate to={routes.accounts.path} replace />
       ) : accessData.overlayNotProvisioned === true
        && isUIconvergence === false
        && !isAccountsPage ? (
        <OverlayNotProvisioned />
      ) : accessData.unauthorized === true ? (
        <Unauthorized />
      ) : accessData.loader === true ? (
        <div className="home-spinner">
          <Spinner />
        </div>
      ) : accessData.error !== null ? (
        <ErrorComponent
          {...accessData.error}
          className="large-dashlet-error"
          imgClassName="error-img-styling"
        />
      ) : (
        <div id="main-content">
          {accessData.sidebar === true && (
            <MegaMenu setAltoProps={setAltoProps} />
          )}
          {pageProps.workflowContainer === true ? (
            <GridContainerWorkflow {...pageProps} accessData={accessData} />
          ) : (
            <GridContainer {...pageProps} accessData={accessData} />
          )}
        </div>
      )}
    </>
  );
};

Authorization.propTypes = {
  globalFilter: PropTypes.object.isRequired,
  pageProps: PropTypes.object.isRequired,
  setAltoProps: PropTypes.func.isRequired
};

export default AppHOC(Authorization);
